import { BaseTracker, SubmitStatus, SubmitItem, SubmitItemResult } from '../types/submitSequence';

export const addToSequence = <TContext extends BaseTracker, TResponse>(
  sequence: SubmitItem<TContext>[],
  item: SubmitItem<TContext, TResponse>
) => sequence.push(item);

export type SubmitChainResult<T extends BaseTracker> = {
  tracker: T;
  results: SubmitItemResult<T>[];
};

export const buildSubmitPromiseChain = <TTracker extends BaseTracker = BaseTracker>(
  initialTracker: TTracker,
  submitSequence: SubmitItem<TTracker>[],
  itemProcesser: (item: SubmitItem<TTracker>, ctx: TTracker) => Promise<SubmitStatus>
): Promise<SubmitChainResult<TTracker>> => {
  const initialItemResults: SubmitItemResult<TTracker>[] = [];
  let promiseChain = Promise.resolve({ tracker: initialTracker, results: initialItemResults });
  submitSequence.forEach((item) => {
    promiseChain = promiseChain.then(async ({ tracker, results }) => {
      // skip if previous item in queue has failed
      if (tracker.errors) {
        const cancelledResult: SubmitItemResult<TTracker> = { item };
        return { tracker, results: [...results, cancelledResult] };
      }

      let itemResult = item.statusSelector?.(tracker);

      if (!itemResult?.success) {
        // perform API call if not already completed successfully
        itemResult = await itemProcesser(item, tracker);
      }
      if (itemResult.success && item.processSuccessResult) {
        itemResult = item.processSuccessResult(itemResult.result, tracker);
      }
      if (!itemResult.success) {
        tracker = { ...tracker, errors: itemResult.errors };
      }
      if (item.statusSetter) {
        tracker = item.statusSetter?.(tracker, itemResult);
      }
      return { tracker, results: [...results, { item, status: itemResult }] };
    });
  });
  return promiseChain;
};
